import React, {Dispatch, SetStateAction, useEffect, useState} from 'react'
import {Table, Dropdown, Form, Pagination} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import threeDots from '../../../../_metronic/assets/images/svg/investor/threeDots.svg'
import {getNumberOfDays} from '../core/_utils'
import {useDateFormat} from '../../../hooks/useDateFormat'
import {FormikProps} from 'formik'
import {DocumentFormValues} from '../core/_models'
import {isMobile} from 'react-device-detect'

const DocumentPreview = ({
  formik,
  templateDetails,
  setCurrentDocumentStep,
  setSignatoryRole,
  setIsEdited,
  templateId,
}: {
  formik: FormikProps<DocumentFormValues>
  templateDetails: any
  setCurrentDocumentStep: Dispatch<SetStateAction<number>>
  setSignatoryRole: Dispatch<SetStateAction<string | undefined>>
  setIsEdited: Dispatch<SetStateAction<boolean>>
  templateId: number | undefined
}) => {
  const {formatMessage} = useIntl()
  const [totalPages, setTotalPages] = useState(0)
  const [documentSubmissionCurrentPage, setDocumentSubmissionCurrentPage] = useState(1)
  const [limitPerPage, setLimitPerPage] = useState(10)
  const {getDateValue} = useDateFormat()

  useEffect(() => {
    const totalItems = formik.values.signatories.length
    setTotalPages(Math.ceil(totalItems / limitPerPage))
  }, [formik.values.signatories.length, limitPerPage])

  const renderPaginationItems = () => {
    const items = []
    const visiblePageCount = 5 // Number of visible page numbers

    let startPage = Math.max(1, documentSubmissionCurrentPage - Math.floor(visiblePageCount / 2))
    let endPage = Math.min(totalPages, startPage + visiblePageCount - 1)

    if (endPage - startPage + 1 < visiblePageCount) {
      startPage = Math.max(1, endPage - visiblePageCount + 1)
    }

    items.push(
      <Pagination.First
        key='first'
        disabled={documentSubmissionCurrentPage === 1}
        onClick={() => handleClick(1)}
      />
    )
    items.push(
      <Pagination.Prev
        key='prev'
        disabled={documentSubmissionCurrentPage === 1}
        onClick={() => handleClick(documentSubmissionCurrentPage - 1)}
      />
    )

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === documentSubmissionCurrentPage}
          onClick={() => handleClick(page)}
        >
          {page}
        </Pagination.Item>
      )
    }

    if (startPage > 1) {
      items.splice(2, 0, <Pagination.Ellipsis key='ellipsis1' />)
    }
    if (endPage < totalPages) {
      items.splice(items.length, 0, <Pagination.Ellipsis key='ellipsis2' />)
    }

    items.push(
      <Pagination.Next
        key='next'
        disabled={documentSubmissionCurrentPage === totalPages}
        onClick={() => handleClick(documentSubmissionCurrentPage + 1)}
      />
    )
    items.push(
      <Pagination.Last
        key='last'
        disabled={documentSubmissionCurrentPage === totalPages}
        onClick={() => handleClick(totalPages)}
      />
    )

    return items
  }

  const handleClick = (pageNumber: number) => {
    setDocumentSubmissionCurrentPage(pageNumber)
  }

  const startIndex = (documentSubmissionCurrentPage - 1) * limitPerPage
  const endIndex = startIndex + limitPerPage
  const currentSignatories = formik.values.signatories.slice(startIndex, endIndex)

  return (
    <div>
      <div className='d-flex flex-column flex-md-row mb-10 '>
        <div className='preview-custom-margin mb-4 mb-md-0'>
          <p className={`mb-0 ${isMobile ? 'font-size-16' : 'font-size-20'}  fw-bold `}>
            {formik.values.title}
          </p>
          <p className={`mb-0 ${isMobile ? 'font-size-13' : 'font-size-14'} text-clra8b `}>
            {formatMessage({id: 'Document Name'})}
          </p>
        </div>
        <div className='preview-custom-margin mb-4 mb-md-0'>
          {templateId ? (
            <p className={`mb-0 ${isMobile ? 'font-size-16' : 'font-size-20'} fw-bold `}>
              {templateDetails?.templateName || templateDetails?.title}
            </p>
          ) : (
            <div className={`mb-0 ${isMobile ? 'font-size-16' : 'font-size-20'} fw-bold  `}>-</div>
          )}

          <p className={`mb-0 ${isMobile ? 'font-size-13' : 'font-size-14'} text-clra8b `}>
            {formatMessage({id: 'Template Used'})}
          </p>
        </div>
        <div className='preview-custom-margin mb-4 mb-md-0'>
          <p className={`mb-0 ${isMobile ? 'font-size-16' : 'font-size-20'} fw-bold `}>
            {formik.values.expiryDate
              ? formatMessage({id: `${getNumberOfDays(formik.values.expiryDate as Date)} Days`})
              : formatMessage({id: 'No Expiry'})}
          </p>
          <p className={`mb-0 ${isMobile ? 'font-size-13' : 'font-size-14'} text-clra8b `}>
            {formatMessage({id: 'Validity'})}
          </p>
        </div>
        <div className='mb-4 mb-md-0'>
          <p className={`mb-0 ${isMobile ? 'font-size-16' : 'font-size-20'} fw-bold `}>
            {formik.values.signatories.length}
          </p>
          <p className={`mb-0 ${isMobile ? 'font-size-13' : 'font-size-14'} text-clra8b `}>
            {formatMessage({id: 'Number of signatories'})}
          </p>
        </div>
      </div>
      <div className='digital-signing-wrapper p-0'>
        <div className='table table-responsive h-100'>
          <Table className='w-100'>
            <thead className='custom-thead border-bottom'>
              <tr className='font-size-12 font-weight-400 table-row border-dark-mode'>
                <td className='title-width-mobile'>{formatMessage({id: 'Parties'})}</td>
                <td>{formatMessage({id: 'Name'})}</td>
                <td>{formatMessage({id: 'Email'})}</td>
                <td>{formatMessage({id: 'Due Date'})}</td>
                <td className='d-flex justify-content-end border-0 me-5'>
                  {formatMessage({id: 'Actions'})}
                </td>
              </tr>
            </thead>
            <tbody>
              {currentSignatories.map((signatory: any, index: number) => (
                <tr className='border-bottom' key={index}>
                  <td className='pt-4 '>
                    <p className='text-width-mobile mb-0'>{signatory.role}</p>
                  </td>
                  <td className='pt-4'>
                    <p className='text-width-mobile mb-0'>{signatory.name}</p>
                  </td>
                  <td className='pt-4'>
                    {' '}
                    <p className=' mb-0'>{signatory.email}</p>
                  </td>
                  <td className='pt-4'>
                    <p className='text-width-mobile mb-0'>
                      {signatory?.dueDate
                        ? getDateValue(new Date(signatory?.dueDate).toISOString()?.toString())
                        : '-'}
                    </p>
                  </td>
                  <td className='pt-3'>
                    <div className='dropdown-container d-flex justify-content-end me-8'>
                      <Dropdown>
                        <Dropdown.Toggle className='btn btn-white table-three-dots dots-container'>
                          <img src={threeDots} alt='' className='three-dots-image' />
                        </Dropdown.Toggle>
                        <Dropdown.Menu className='m-2'>
                          <Dropdown.Item
                            className='text-dark ps-5'
                            onClick={() => {
                              setIsEdited(true)
                              setCurrentDocumentStep(3)
                              setSignatoryRole(signatory.role)
                            }}
                          >
                            {formatMessage({id: 'Edit Recipient'})}
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {formik.values.signatories.length > 10 && (
            <div className='w-100 d-flex justify-content-between align-items-center flex-wrap flex-md-nowrap'>
              <div className='d-flex align-items-center w-100 w-md-50'>
                <Form.Select
                  aria-label='Default select example'
                  onChange={(e) => {
                    setLimitPerPage(parseInt(e.target.value))
                    setDocumentSubmissionCurrentPage(1)
                  }}
                  defaultValue={limitPerPage}
                >
                  <option value={10}>{formatMessage({id: '10'})}</option>
                  <option value={20}>{formatMessage({id: '20'})}</option>
                  <option value={50}>{formatMessage({id: '50'})}</option>
                  <option value={100}>{formatMessage({id: '100'})}</option>
                </Form.Select>
                <span className='font-size-13 ms-4'>
                  {formatMessage({
                    id: `Showing ${startIndex + 1} to ${Math.min(
                      endIndex,
                      formik.values.signatories.length
                    )} of ${formik.values.signatories.length} records`,
                  })}
                </span>
              </div>
              <div>
                <Pagination>{renderPaginationItems()}</Pagination>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default DocumentPreview
